import { proxy, useSnapshot } from 'valtio'
import { set, child, onValue } from 'firebase/database'

import { copy } from '../../global/utils/valtio'
import * as firebaseConnection from '../firebaseConnection'
import type { Layout, Mix } from '../model/mix'

const ROOT_NODE = 'mixes'

const mixesState = proxy<{
  mixes: Mix[]
  editingMix: Mix | null
  editingFeed: {
    idx: number | null
    mode: 'initial' | 'swap'
  }
}>({
  mixes: [],
  editingMix: null,
  editingFeed: {
    idx: null,
    mode: 'initial',
  },
})

// Subscribe
firebaseConnection.runWhenReady((connection) => {
  // Adds a Firebase listener that triggers when the mixes change.
  onValue(child(connection.room, ROOT_NODE), (snapshot) => {
    const mixes: Mix[] = snapshot.val() ?? []
    mixesState.mixes = mixes.map((m) => {
      return {
        ...m,
        // firebase deletes empty arrays
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        feeds: m.feeds ?? [],
        // firebase deletes empty arrays
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        elements: m.elements ?? [],
      }
    })
  })
})

const addNewMix = async (layout: Layout) => {
  const connection = await firebaseConnection.get()

  const elements = layout.elements.map((el, index) => ({ ...el, index }))

  const mix = {
    id: crypto.randomUUID(),
    name: layout.name ?? '',
    settings: layout.settings,
    elements: elements.filter(
      (el) => el.type === 'graphic' || el.type === 'text'
    ),
    feeds: elements
      .filter(
        (el) => el.type === 'camera' || el.type === 'media' || el.type === 'all'
      )
      .map((l) => ({
        ...l,
        type: l.type as 'camera' | 'media' | 'all',
        media: undefined,
        crop: {
          zoom: l.type === 'media' ? 0 : 30,
          centerX: 0,
          centerY: 0,
        },
      })),
  } satisfies Mix

  // removing undefined values as firebase's realtimeDB doesn't like them
  const newMixes = copy([...mixesState.mixes, mix])

  // optimistic update
  mixesState.mixes = newMixes

  await set(child(connection.room, ROOT_NODE), newMixes).catch(
    (err: unknown) => {
      connection.logError('ADD_NEW_MIX', 'Failed to add new mix', err)
    }
  )

  return mix.id
}

const removeMix = async (mixId?: string) => {
  const mixIdToRemove = mixId ?? mixesState.editingMix?.id

  if (!mixIdToRemove) return Promise.reject(new Error('No mix id provided'))

  const mixIndex = mixesState.mixes.findIndex((m) => m.id === mixId)
  if (mixIndex === -1)
    return Promise.reject(new Error("Couldn't find mix to remove"))

  const connection = await firebaseConnection.get()

  const newMixes = mixesState.mixes.filter((_, index) => index !== mixIndex)
  setEditingMix(null)

  return set(child(connection.room, ROOT_NODE), newMixes).catch(
    (err: unknown) => {
      connection.logError('REMOVE_MIX', 'Failed to remove mix', err)
    }
  )
}

const updateMix = async (mixId: string, mix: Mix) => {
  const connection = await firebaseConnection.get()

  const mixIndex = mixesState.mixes.findIndex((m) => m.id === mixId)
  if (mixIndex === -1)
    return Promise.reject(new Error("Couldn't find mix to update"))

  const newMixes = mixesState.mixes.map((m, index) =>
    index === mixIndex ? mix : m
  )

  return set(child(connection.room, ROOT_NODE), newMixes).catch(
    (err: unknown) => {
      connection.logError('UPDATE_MIX', 'Failed to update mix', err)
    }
  )
}

const setEditingMix = (mixId: string | null) => {
  if (mixId === null) {
    mixesState.editingMix = null
  } else {
    mixesState.editingMix = copy(
      mixesState.mixes.find((mix) => mix.id === mixId) ?? null
    )
  }

  mixesState.editingFeed = {
    idx: null,
    mode: 'initial',
  }
}

const setEditingFeed = (
  idx: number | null,
  mode: typeof mixesState.editingFeed.mode = 'initial'
) => {
  mixesState.editingFeed = {
    idx,
    mode,
  }
}

const updateEditingMix = (mix: Mix) => {
  mixesState.editingMix = mix
}

const updateEditingMixName = (newName: string) => {
  if (!mixesState.editingMix) return
  mixesState.editingMix.name = newName
}

const saveEditingMix = () => {
  if (!mixesState.editingMix)
    return Promise.reject(new Error("Can't save, no mix is being edited."))

  return updateMix(mixesState.editingMix.id, mixesState.editingMix)
}

const useMixes = () => {
  const { mixes } = useSnapshot(mixesState)

  return mixes
}

const useEditingMixState = () => {
  const { editingMix } = useSnapshot(mixesState)

  return editingMix
}

const getEditingMix = () => {
  return mixesState.editingMix
}

const useEditingMixFeed = () => {
  const { editingFeed } = useSnapshot(mixesState)
  return editingFeed
}

const getEditingFeedIdx = () => {
  return mixesState.editingFeed.idx
}

const useIsEditingAnyMix = () => {
  const editingMix = useEditingMixState()
  return !!editingMix
}

const useIsEditingMix = (mixId: string) => {
  const editingMix = useEditingMixState()

  return editingMix?.id === mixId
}

export {
  addNewMix,
  removeMix,
  updateMix,
  useMixes,
  setEditingMix,
  setEditingFeed,
  updateEditingMix,
  updateEditingMixName,
  saveEditingMix,
  useEditingMixState,
  getEditingMix,
  useEditingMixFeed,
  getEditingFeedIdx,
  useIsEditingMix,
  useIsEditingAnyMix,
}
